import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, Copy, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SeatMap from "@/components/SeatMap";
import StopsEditor from "@/components/StopsEditor";
import SeatPlanEditor from "@/components/SeatPlanEditor";
import SearchSelect from "@/components/SearchSelect";
import FareTable, { fareRows } from "@/components/FareTable";
import { WeekdayPicker, TimesEditor } from "@/components/ScheduleInputs";
import LocationPicker from "@/components/LocationPicker";
import { api, errMsg } from "@/lib/api";

const PRESETS = [12, 14, 18, 22, 33, 45, 50];
const PAGE_SIZE = 15;

function FieldInput({ f, value, onChange, form }) {
  const id = `field-${f.key}`;
  if (f.type === "stops") return <StopsEditor value={value} onChange={onChange} options={f.options} form={form} />;
  if (f.type === "plan") return <SeatPlanEditor value={value} onChange={onChange} />;
  if (f.type === "map") return <LocationPicker form={form} onChange={onChange} />;
  if (f.type === "fares") return <FareTable form={form} onChange={onChange} options={f.options} />;
  if (f.type === "weekdays") return <WeekdayPicker value={value} onChange={onChange} />;
  if (f.type === "times") return <TimesEditor value={value} onChange={onChange} />;
  if (f.type === "switch") {
    return <div className="flex items-center gap-3"><Switch checked={!!value} onCheckedChange={onChange} data-testid={id} /><span className="text-sm font-semibold">{value ? (f.on || "Active") : (f.off || "Not active")}</span></div>;
  }
  if (f.type === "search") return <SearchSelect value={value} onChange={onChange} options={f.options} placeholder={f.placeholder || `Search ${f.label.toLowerCase()}`} testid={id} />;
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={id}><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (f.type === "multi") {
    const list = value || [];
    return (
      <div className="flex flex-wrap gap-2" data-testid={id}>
        {f.options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o])}
            className={`chip btn-press border px-3 py-1.5 ${list.includes(o) ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`} data-testid={`${id}-${o.replace(/\s/g, "-")}`}>{f.optionLabel ? f.optionLabel(o) : o}</button>
        ))}
      </div>
    );
  }
  if (f.type === "capacity") {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((n) => (
            <button key={n} type="button" onClick={() => onChange(n)} data-testid={`capacity-preset-${n}`}
              className={`chip btn-press border px-3 py-1.5 ${Number(value) === n ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{n} seats</button>
          ))}
          <Input type="number" min={4} max={70} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-8 w-24" data-testid={id} />
        </div>
        {Number(value) >= 4 && Number(value) <= 70 && <div className="text-center"><div className="label-xs mb-2">Seat layout preview (what passengers will see)</div><SeatMap capacity={Number(value)} layout={form?.layout} compact /></div>}
      </div>
    );
  }
  if (f.type === "password") return <PasswordInput value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} testid={id} />;
  return <Input type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} data-testid={id} />;
}

export default function ResourceManager({ title, endpoint, fields, columns, onChanged, side, wide, derive, duplicate, rowActions, asyncDerive, filters, note, search, searchPlaceholder }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fv, setFv] = useState(() => Object.fromEntries((filters || []).filter((f) => f.defaultValue).map((f) => [f.key, f.defaultValue])));
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const one = title.replace(/(us)es$|s$/, "$1");

  const load = useCallback(() => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, fv]);

  const shown = items && filters ? items.filter((it) => filters.every((f) => !fv[f.key] || f.match(it, fv[f.key]))) : items;
  const searched = search && q.trim() ? shown?.filter((it) => (search(it) || "").toLowerCase().includes(q.trim().toLowerCase())) : shown;
  const total = searched?.length || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const paged = searched?.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  const blank = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "multi" ? [] : "")]));
  const save = async () => {
    setBusy(true);
    const body = { ...form };
    fields.forEach((f) => {
      if (["number", "capacity"].includes(f.type)) body[f.key] = Number(body[f.key] || 0);
      if (f.type === "password" && !body[f.key]) body[f.key] = null;
      if (f.type === "stops") body[f.key] = (body[f.key] || []).map((s) => ({ station_id: s.station_id, minutes: Number(s.minutes || 0), distance_km: Number(s.distance_km || 0) }));
      if (f.type === "fares") body[f.key] = fareRows(form).map((r) => ({ ...r, fare: Number(r.fare || 0) }));
      if (f.type === "times") body[f.key] = (body[f.key] || []).filter(Boolean);
    });
    try {
      if (form.id) await api.put(`/admin/${endpoint}/${form.id}`, body); else await api.post(`/admin/${endpoint}`, body);
      toast.success(`${one} saved`);
      setForm(null); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-4" data-testid={`resource-${endpoint}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">{title}</h2>
        <Button onClick={() => setForm(blank())} className="rounded-full bg-[#0052FF] hover:bg-[#0041CC] btn-press" data-testid={`add-${endpoint}-button`}><Plus className="h-4 w-4" />Add</Button>
      </div>
      {note && <p className="rounded-xl bg-blue-50 px-3.5 py-2.5 text-sm text-slate-600" data-testid={`resource-${endpoint}-note`}>{note}</p>}
      {(filters || search) && (
        <div className="flex flex-wrap items-end gap-3" data-testid={`${endpoint}-controls`}>
          {filters?.map((f) => (
            <div key={f.key} className="min-w-[180px] space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
              <Select value={fv[f.key] || "__all__"} onValueChange={(v) => setFv((p) => ({ ...p, [f.key]: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="h-10 max-w-[240px] [&>span]:truncate" data-testid={`${endpoint}-filter-${f.key}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">{f.allLabel || "All"}</SelectItem>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
          {search && (
            <div className="min-w-[240px] flex-1 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder || "Search"} className="h-10 pl-9" data-testid={`${endpoint}-search`} />
              </div>
            </div>
          )}
        </div>
      )}
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{columns.map((c) => <th key={c.label} className="whitespace-nowrap px-4 py-3 font-bold">{c.label}</th>)}<th className="px-4 py-3">Active</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={9} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[#0052FF]" /></td></tr>}
            {paged?.map((it) => (
              <tr key={it.id} className={`border-t border-slate-100 ${it.active === false ? "opacity-50" : ""}`} data-testid={`${endpoint}-row-${it.id}`}>
                {columns.map((c) => <td key={c.label} className={`px-4 py-3 ${c.nowrap ? "whitespace-nowrap" : ""}`}>{c.render ? c.render(it) : it[c.key]}</td>)}
                <td className="px-4 py-3"><Switch checked={it.active !== false} onCheckedChange={() => act(() => api.patch(`/admin/${endpoint}/${it.id}/toggle`), "Status updated")} data-testid={`${endpoint}-toggle-${it.id}`} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...blank(), ...it, password: "" })} data-testid={`${endpoint}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  {duplicate && <Button size="icon" variant="ghost" title="Duplicate" onClick={() => { const { id, created_at, updated_at, generated_until, ...rest } = it; setForm({ ...blank(), ...rest, ...duplicate(it) }); }} data-testid={`${endpoint}-duplicate-${it.id}`}><Copy className="h-4 w-4" /></Button>}
                  {rowActions?.(it)}
                </td>
              </tr>
            ))}
            {items?.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Nothing here yet</td></tr>}
            {items?.length > 0 && total === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">No records match your search or filters</td></tr>}
          </tbody>
        </table>
      </div>
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between px-1 text-sm text-slate-600" data-testid={`${endpoint}-pager`}>
          <span data-testid={`${endpoint}-pager-info`}>Showing {(cur - 1) * PAGE_SIZE + 1}-{Math.min(cur * PAGE_SIZE, total)} of {total}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-full" disabled={cur <= 1} onClick={() => setPage(cur - 1)} data-testid={`${endpoint}-prev`}><ChevronLeft className="h-4 w-4" />Prev</Button>
            <span className="font-semibold">Page {cur} of {pages}</span>
            <Button size="sm" variant="outline" className="rounded-full" disabled={cur >= pages} onClick={() => setPage(cur + 1)} data-testid={`${endpoint}-next`}>Next<ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto rounded-3xl ${side ? "sm:max-w-4xl" : wide ? "sm:max-w-2xl" : "sm:max-w-lg"}`} data-testid={`${endpoint}-form-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{form?.id ? "Edit" : form?.copied ? "Duplicate" : "Add"} {one.toLowerCase()}</DialogTitle></DialogHeader>
          {form && (
            <div className={side ? "grid gap-6 md:grid-cols-[1fr_auto]" : ""}>
            <div className="space-y-4">
              {fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-600">{f.label}{f.type === "password" && form.id ? " (leave blank to keep)" : ""}</Label>
                  <FieldInput f={f} value={form[f.key]} onChange={(v) => {
                      const merge = (p) => (f.type === "map" ? { ...p, ...v } : { ...p, [f.key]: v });
                      setForm((p) => (derive ? derive(merge(p), f.key) : merge(p)));
                      if (asyncDerive) asyncDerive(merge(form), f.key, form).then((m) => m && setForm((p) => (p ? m(p) : p)));
                    }} form={form} />
                </div>
              ))}
              <Button disabled={busy} onClick={save} className="h-11 w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC] btn-press" data-testid={`${endpoint}-save-button`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            {side && <div className="md:sticky md:top-0 md:self-start" data-testid={`${endpoint}-side-panel`}>{side(form)}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
